import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { UserLayout } from '../../components/user/UserLayout';
import { ArrowLeft, User, Armchair, Clock, Hash, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const STATUS_COLORS = {
  'Order Placed': 'bg-blue-100 text-blue-700',
  'Order Accepted': 'bg-indigo-100 text-indigo-700',
  'In Preparation': 'bg-amber-100 text-amber-700',
  'Ready to Pick': 'bg-purple-100 text-purple-700',
  'Picked Up': 'bg-cyan-100 text-cyan-700',
  'Delivered': 'bg-green-100 text-green-700',
  'Cancelled by Customer': 'bg-red-100 text-red-700',
  'Cancelled By Staff': 'bg-red-100 text-red-700',
};

const NEXT_STATUS = {
  'Order Placed': 'Order Accepted',
  'Order Accepted': 'In Preparation',
  'In Preparation': 'Ready to Pick',
  'Ready to Pick': 'Picked Up',
  'Picked Up': 'Delivered',
};

export const OrderDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/orders/${orderId}`);
      if (response.data.code === 200) {
        setOrder(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching order:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (newStatus) => {
    setUpdating(true);
    try {
      const response = await axios.put(`${BACKEND_URL}/api/orders/${orderId}/status`, { status: newStatus });
      if (response.data.code === 200) {
        setOrder(prev => ({ ...prev, status: newStatus }));
        toast({ title: 'Status Updated', description: `Order moved to "${newStatus}"` });
      } else {
        toast({ title: 'Error', description: response.data.message, variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update status', variant: 'destructive' });
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <UserLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </UserLayout>
    );
  }

  if (!order) {
    return (
      <UserLayout>
        <div className="text-center py-20">
          <p className="text-slate-500">Order not found</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </UserLayout>
    );
  }

  const kitchenItems = (order.items || []).filter(i => (i.orderType || 'Kitchen') === 'Kitchen');
  const barItems = (order.items || []).filter(i => i.orderType === 'Drink' || i.orderType === 'Bar');
  const kitchenSubtotal = kitchenItems.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const barSubtotal = barItems.reduce((sum, i) => sum + i.price * i.quantity, 0);

  return (
    <UserLayout>
      <div className="space-y-6 max-w-4xl">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-800">Order #{order.id}</h1>
              <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${STATUS_COLORS[order.status] || 'bg-slate-100 text-slate-600'}`}>
                {order.status}
              </span>
              <span className={`text-xs px-2 py-1 rounded font-medium ${order.orderType === 'Bar' ? 'bg-cyan-100 text-cyan-700' : 'bg-orange-100 text-orange-700'}`}>
                {order.orderType || 'Kitchen'}
              </span>
            </div>
            <p className="text-slate-500 text-sm mt-1">
              {new Date(order.createdAt).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
          {/* Status Actions */}
          {NEXT_STATUS[order.status] && (
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                disabled={updating}
                onClick={() => updateStatus(NEXT_STATUS[order.status])}
                data-testid="advance-status-btn"
              >
                {NEXT_STATUS[order.status]}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-red-600 border-red-200 hover:bg-red-50"
                disabled={updating}
                onClick={() => updateStatus('Cancelled By Staff')}
                data-testid="cancel-order-btn"
              >
                Cancel
              </Button>
            </div>
          )}
        </div>

        {/* Customer & Allocation Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
            <div className="flex items-center gap-2 mb-3">
              <User className="w-4 h-4 text-slate-400" />
              <span className="text-sm font-medium text-slate-500">Guest</span>
            </div>
            <p className="text-lg font-semibold text-slate-800">{order.guestName || 'Guest'}</p>
            <p className="text-sm text-slate-500 mt-1">Room {order.roomNumber || '—'}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
            <div className="flex items-center gap-2 mb-3">
              <Armchair className="w-4 h-4 text-slate-400" />
              <span className="text-sm font-medium text-slate-500">Allocation</span>
            </div>
            <p className="text-sm text-slate-700">Seats: {order.seatIds?.join(', ') || '—'}</p>
            <p className="text-sm text-slate-500 mt-1">Guests: {order.guestCount || '—'}</p>
            {order.allocationId && (
              <button
                onClick={() => navigate(`/user/allocations/${order.allocationId}`)}
                className="text-xs text-teal-600 hover:underline mt-2"
              >
                View Allocation
              </button>
            )}
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-4 h-4 text-slate-400" />
              <span className="text-sm font-medium text-slate-500">Details</span>
            </div>
            <p className="text-sm text-slate-700">Device: {order.deviceId || '—'}</p>
            <p className="text-sm text-slate-500 mt-1">Status: {order.allocationStatus || '—'}</p>
          </div>
        </div>

        {/* Items Tables */}
        <div className="space-y-6">
          {/* Kitchen Items */}
          {kitchenItems.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="bg-orange-50 border-b border-orange-200 px-6 py-3 flex items-center justify-between">
                <span className="font-semibold text-orange-700">Kitchen Items</span>
                <span className="text-sm font-medium text-orange-600">{kitchenItems.length} items</span>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 text-left">
                    <th className="px-6 py-3 font-medium text-slate-500 w-12">#</th>
                    <th className="px-6 py-3 font-medium text-slate-500">Item</th>
                    <th className="px-6 py-3 font-medium text-slate-500 text-center">Qty</th>
                    <th className="px-6 py-3 font-medium text-slate-500 text-right">Price</th>
                    <th className="px-6 py-3 font-medium text-slate-500 text-right">Subtotal</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {kitchenItems.map((item, idx) => (
                    <tr key={idx}>
                      <td className="px-6 py-3 text-slate-400">{idx + 1}</td>
                      <td className="px-6 py-3">
                        <span className="text-slate-800 font-medium">{item.menuItemName}</span>
                        {item.notes && (
                          <p className="text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded mt-1 italic inline-block">{item.notes}</p>
                        )}
                      </td>
                      <td className="px-6 py-3 text-slate-700 text-center">{item.quantity}</td>
                      <td className="px-6 py-3 text-slate-600 text-right">${item.price?.toFixed(2)}</td>
                      <td className="px-6 py-3 text-slate-800 font-medium text-right">${(item.price * item.quantity).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-slate-200 bg-slate-50">
                    <td colSpan={4} className="px-6 py-3 text-right font-medium text-slate-600">Kitchen Subtotal</td>
                    <td className="px-6 py-3 text-right font-bold text-slate-800">${kitchenSubtotal.toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Bar Items */}
          {barItems.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="bg-cyan-50 border-b border-cyan-200 px-6 py-3 flex items-center justify-between">
                <span className="font-semibold text-cyan-700">Bar Items</span>
                <span className="text-sm font-medium text-cyan-600">{barItems.length} items</span>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 text-left">
                    <th className="px-6 py-3 font-medium text-slate-500 w-12">#</th>
                    <th className="px-6 py-3 font-medium text-slate-500">Item</th>
                    <th className="px-6 py-3 font-medium text-slate-500 text-center">Qty</th>
                    <th className="px-6 py-3 font-medium text-slate-500 text-right">Price</th>
                    <th className="px-6 py-3 font-medium text-slate-500 text-right">Subtotal</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {barItems.map((item, idx) => (
                    <tr key={idx}>
                      <td className="px-6 py-3 text-slate-400">{idx + 1}</td>
                      <td className="px-6 py-3">
                        <span className="text-slate-800 font-medium">{item.menuItemName}</span>
                        {item.notes && (
                          <p className="text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded mt-1 italic inline-block">{item.notes}</p>
                        )}
                      </td>
                      <td className="px-6 py-3 text-slate-700 text-center">{item.quantity}</td>
                      <td className="px-6 py-3 text-slate-600 text-right">${item.price?.toFixed(2)}</td>
                      <td className="px-6 py-3 text-slate-800 font-medium text-right">${(item.price * item.quantity).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-slate-200 bg-slate-50">
                    <td colSpan={4} className="px-6 py-3 text-right font-medium text-slate-600">Bar Subtotal</td>
                    <td className="px-6 py-3 text-right font-bold text-slate-800">${barSubtotal.toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Total */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 flex items-center justify-between">
            <span className="text-lg font-semibold text-slate-700">Order Total</span>
            <span className="text-2xl font-bold text-slate-900">${order.totalAmount?.toFixed(2)}</span>
          </div>
        </div>
      </div>
    </UserLayout>
  );
};

export default OrderDetails;
